import {
  deleteScanOrderPallet,
  downloadScanPalletCartonLabel,
  downloadScanPalletLabel,
  downloadScanShippingLabel,
  OrderAPI,
} from "@/apis/tms/order";
import Indicator from "@/components/shared/Indicator";
import PageHeader from "@/components/shared/PageHeader";
import { BasicInfo } from "@/components/tms/order/process/BasicInfo";
import PalletAddDialog from "@/components/tms/pallet/AddDialog";
import PalletScanProducts from "@/components/tms/pallet/scan";
import TMSPalletsTable from "@/components/tms/pallet/table";
import {
  OrderProcessPalletCols,
  PalletsTableRef,
} from "@/components/tms/pallet/table/helper";
import { IntegrationOrderTypeColorMap } from "@/modal/integration/order/const";
import { SortDirection } from "ag-grid-community";
import {
  Cube2BOrder,
  Cube2COrder,
  DialogRef,
  IntegrationOrderType,
  TMSOrderStatus,
  TMSPalletStatus,
  TMSPalletVo,
  TMSScanOrderVo,
  TMSShippingLabelMode,
} from "@/types";
import {
  CheckOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EyeFilled,
  PlusOutlined,
  PrinterOutlined,
  ReloadOutlined,
  ScanOutlined,
} from "@ant-design/icons";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { Alert, Button, Input, Tag } from "antd";
import classNames from "classnames";
import { useCallback, useMemo, useRef, useState, useEffect } from "react";
import { StringParam, useQueryParam, withDefault } from "use-query-params";
import PrinterConfig, { PrinterRef } from "@/components/printer/PrinterConfig";
import { useGlobalContext } from "@/hooks/context";
import CartonList from "@/components/tms/order/process/cartons";
import { getTMSOrder, PalletDimensionCol } from "./helper";
import {
  OrderLoadingDialog,
  OrderLoadResult,
} from "@/components/tms/order/process/CheckProcessOrder";
import { ScanOrderCartonsRef } from "@/components/tms/order/process/cartons/helper";
import { ScanInfo } from "@/components/tms/order/process/ScanInfo";
import PdfViewerModal from "@/components/shared/PdfViewer";
import ShippingLabelInfo, {
  ShippingLabelInfoRef,
} from "@/components/tms/order/process/ShippingLabelInfo";
import { wrapPage } from "@/utils/pages";
import PalletCompleteConfirmDialog, {
  PalletCompleteDialogOpenProps,
} from "@/components/tms/pallet/scan/cofirm";
import { OrderStatusColorMap } from "@/modal/tms/order/const";
import Compact from "antd/es/space/Compact";

export function ProcessScanOrderPageFC() {
  const { hintPrompt, openConfirm } = useGlobalContext();
  const [riiOrderNo, setRIIOrderNo] = useQueryParam(
    "riiOrderNo",
    withDefault(StringParam, ""),
  );
  const [tmsOrder, setTMSOrder] = useState<TMSScanOrderVo | null>(null);
  const [cubeOrder, setCubeOrder] = useState<Cube2COrder | Cube2BOrder | null>(
    null,
  );
  const [orderLoaded, setOrderLoaded] = useState(false);

  useEffect(() => {
    if (!riiOrderNo) {
      setTMSOrder(null);
      setCubeOrder(null);
      setOrderLoaded(false);
    }
  }, [riiOrderNo]);

  const onOrderLoaded = useCallback((result: OrderLoadResult) => {
    setTMSOrder(result.tmsOrder);
    setCubeOrder(result.cubeOrder);
    setOrderLoaded(true);
  }, []);

  const refetchTMSOrder = useCallback(() => {
    if (riiOrderNo) {
      getTMSOrder(riiOrderNo).then(setTMSOrder);
    }
  }, [riiOrderNo]);

  const [printingLabel, setPrintingLabel] = useState(false);

  const palletAddDialogRef = useRef<DialogRef>(null);
  const palletScanProductsDialogRef = useRef<DialogRef>(null);

  const printerConfigRef = useRef<PrinterRef>(null);
  const palletsTableRef = useRef<PalletsTableRef>(null);
  const cartonListRef = useRef<ScanOrderCartonsRef>(null);
  const pdfViewerModalRef = useRef<DialogRef>(null);
  const palletCompleteConfirmDialogRef = useRef<DialogRef>(null);
  const shippingLabelInfoRef = useRef<ShippingLabelInfoRef>(null);

  const orderType = useMemo(() => {
    if (tmsOrder == null) return undefined;
    if (
      tmsOrder.riiOrderNo.startsWith("OTRIIOTIN") ||
      tmsOrder.riiOrderNo.startsWith("OTRIICION")
    ) {
      return IntegrationOrderType.Customer;
    } else if (tmsOrder.riiOrderNo.startsWith("OTRIISUB")) {
      return IntegrationOrderType.Business;
    }
    return null;
  }, [tmsOrder]);

  const customerNumber = useMemo(() => {
    if (cubeOrder == null || orderType == null) return undefined;
    if (orderType === IntegrationOrderType.Customer) {
      return (cubeOrder as Cube2COrder).instruction.theThirdNumber;
    } else if (orderType === IntegrationOrderType.Business) {
      return (cubeOrder as Cube2BOrder).amazonFreightNumber;
    }
    return undefined;
  }, [cubeOrder, orderType]);

  const hasPalletShippingLabel = useMemo(() => {
    return (
      tmsOrder?.scanConfig.shippingLabelMode === TMSShippingLabelMode.Pallet
    );
  }, [tmsOrder?.scanConfig.shippingLabelMode]);

  const pageHeaderText = useMemo(() => {
    if (orderType && customerNumber) {
      let shippingLabelMode = null;
      if (
        tmsOrder?.scanConfig.shippingLabelMode === TMSShippingLabelMode.Pallet
      ) {
        shippingLabelMode = "Pallet Shipping Label";
      } else if (
        tmsOrder?.scanConfig.shippingLabelMode === TMSShippingLabelMode.Carton
      ) {
        shippingLabelMode = "Carton Shipping Label";
      }
      return (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span>{tmsOrder?.riiOrderNo}</span>
            <span>({customerNumber})</span>
            <Tag
              color={IntegrationOrderTypeColorMap[orderType]}
              className="!text-2xl !mr-0"
            >
              {orderType}
            </Tag>
          </div>
          <div className="flex gap-2">
            {tmsOrder?.status && (
              <Tag
                color={OrderStatusColorMap[tmsOrder?.status]}
                className="!text-2xl !mr-0"
              >
                {tmsOrder?.status}
              </Tag>
            )}
            {shippingLabelMode && (
              <Tag color="blue" className="!text-2xl">
                {shippingLabelMode}
              </Tag>
            )}
          </div>
        </div>
      );
    }
    return "Order not found!";
  }, [
    tmsOrder?.riiOrderNo,
    tmsOrder?.status,
    tmsOrder?.scanConfig.shippingLabelMode,
    orderType,
    customerNumber,
  ]);

  const palletQuery = useMemo(() => {
    return {
      orderIds: [tmsOrder?.id ?? "unknown"],
      sorts: ["no"],
      dirs: ["asc"] as SortDirection[],
      includeScannedCartonSkus: true,
      excludeStatusList: [TMSPalletStatus.Deleted],
    };
  }, [tmsOrder]);

  const printPalletLabel = useCallback(
    async (orderId: string, palletId: string) => {
      setPrintingLabel(true);
      return downloadScanPalletLabel(orderId, palletId)
        .then((blob) => {
          return printerConfigRef.current?.printLabel(blob).then(() => {
            hintPrompt(
              "success",
              "Success",
              "Pallet label printed successfully",
            );
          });
        })
        .catch((error) => {
          hintPrompt(
            "error",
            "Failed to print pallet label",
            error.msg ?? "Unknown error",
          );
        })
        .finally(() => {
          setPrintingLabel(false);
        });
    },
    [hintPrompt],
  );

  const printShippingLabel = useCallback(
    async (orderId: string, palletId: string, palletProductId?: string) => {
      setPrintingLabel(true);
      return downloadScanShippingLabel({
        orderIdOrNo: orderId,
        palletId: palletId,
        palletProductId: palletProductId,
      })
        .then((blob) => {
          return printerConfigRef.current?.printLabel(blob).then(() => {
            hintPrompt(
              "success",
              "Success",
              "Shipping label printed successfully",
            );
          });
        })
        .catch((error) => {
          hintPrompt(
            "error",
            "Failed to print shipping label",
            error.msg ?? "Unknown error",
          );
        })
        .finally(() => {
          setPrintingLabel(false);
        });
    },
    [hintPrompt],
  );

  const printCartonLabel = useCallback(
    (orderId: string, palletId: string, scannedCartonId: number) => {
      setPrintingLabel(true);
      return downloadScanPalletCartonLabel(orderId, palletId, scannedCartonId)
        .then((blob) =>
          printerConfigRef.current?.printLabel(blob).then(() => {
            hintPrompt(
              "success",
              "Success",
              "Carton label printed successfully",
            );
          }),
        )
        .catch((error) => {
          hintPrompt(
            "error",
            "Failed to print carton label",
            error.msg ?? error.message ?? "Unknown error",
          );
          throw error;
        })
        .finally(() => {
          setPrintingLabel(false);
        });
    },
    [hintPrompt],
  );

  const deletePallet = useCallback(
    (orderId: string, palletId: string) => {
      openConfirm(
        "Are you sure you want to delete this pallet?",
        () => {
          return deleteScanOrderPallet(orderId, palletId);
        },
        () => {
          hintPrompt("success", "Success", "Pallet deleted successfully");
          palletsTableRef.current?.refresh();
          shippingLabelInfoRef.current?.refresh();
        },
        (error: any) => {
          hintPrompt("error", "Error", error.msg ?? "Unknown error");
        },
      );
    },
    [hintPrompt, openConfirm],
  );

  const onAddPallet = useCallback(
    (orderId: string, palletId: string, openScanDialog: boolean) => {
      palletsTableRef.current?.refresh();
      shippingLabelInfoRef.current?.refresh();

      if (openScanDialog) {
        palletScanProductsDialogRef.current?.showModal({
          palletId: palletId,
          orderId: orderId,
        });
      }
    },
    [],
  );

  const completePalletCb = useCallback(
    async (
      orderId: string,
      palletId: string,
      toPrintPalletLabel: boolean,
      toPrintShippingLabel: boolean,
    ) => {
      palletScanProductsDialogRef.current?.hideModal();
      palletsTableRef.current?.refresh();
      cartonListRef.current?.refresh();
      shippingLabelInfoRef.current?.refresh();
      return new Promise((resolve) => {
        if (toPrintPalletLabel) {
          return printPalletLabel(orderId, palletId).then(() =>
            resolve(toPrintShippingLabel),
          );
        } else {
          resolve(toPrintShippingLabel);
        }
      }).then(() => {
        // if (res) {
        //   return printShippingLabel(orderId, palletId);
        // }
        return Promise.resolve();
      });
    },
    [printPalletLabel],
  );

  const tableActionColumn = useMemo(() => {
    return {
      headerName: "Action",
      minWidth: 250,
      maxWidth: 250,
      resizable: false,
      pinned: "right",
      suppressMenu: true,
      cellRenderer: ({ data }: ICellRendererParams<TMSPalletVo, string>) => {
        return (
          <div className="flex flex-row gap-2">
            <Button
              icon={<EyeFilled />}
              loading={printingLabel}
              title="Preview Pallet Shipping Label"
              onClick={() =>
                pdfViewerModalRef.current?.showModal({
                  url: `${OrderAPI.config.baseURL}/scan/${
                    data!.orderId
                  }/pallets/${data!.id}/label?includeShippingLabel=${tmsOrder?.scanConfig.uploadedPalletShippingLabels ? true : false}`,
                })
              }
            />
            <Button
              icon={<PrinterOutlined />}
              loading={printingLabel}
              title="Print Pallet Shipping Label"
              disabled={
                !hasPalletShippingLabel ||
                !tmsOrder?.scanConfig.uploadedPalletShippingLabels
              }
              onClick={() => printShippingLabel(data!.orderId, data!.id)}
            />
            <Button
              icon={<PrinterOutlined />}
              loading={printingLabel}
              title="Print Pallet Label"
              onClick={() => printPalletLabel(data!.orderId, data!.id)}
            />
            <Button
              icon={<CheckOutlined color="green" />}
              color="green"
              variant="outlined"
              disabled={data!.status !== TMSPalletStatus.Upcoming}
              title="Complete Scan"
              onClick={() =>
                palletCompleteConfirmDialogRef.current?.showModal({
                  palletId: data!.id,
                  palletNo: data!.no,
                  orderId: data!.orderId,
                  requireDimension:
                    tmsOrder?.scanConfig.requirePalletDimension ?? false,
                  printPalletLabel:
                    tmsOrder?.scanConfig.applyPalletLabel ?? false,
                  printShippingLabel:
                    tmsOrder?.scanConfig.shippingLabelMode ===
                    TMSShippingLabelMode.Pallet,
                  completeCb: completePalletCb,
                } as PalletCompleteDialogOpenProps)
              }
            />
            <Button
              icon={<ScanOutlined />}
              title="Scan Goods"
              color="blue"
              variant="solid"
              onClick={() =>
                palletScanProductsDialogRef.current?.showModal({
                  palletId: data!.id,
                  orderId: data!.orderId,
                })
              }
            />
            <Button
              icon={<DeleteOutlined />}
              danger
              title="Delete Pallet"
              onClick={() => deletePallet(data!.orderId, data!.id)}
            />
          </div>
        );
      },
    } as ColDef<TMSPalletVo>;
  }, [
    printPalletLabel,
    printingLabel,
    completePalletCb,
    deletePallet,
    hasPalletShippingLabel,
    printShippingLabel,
    tmsOrder?.scanConfig.applyPalletLabel,
    tmsOrder?.scanConfig.requirePalletDimension,
    tmsOrder?.scanConfig.shippingLabelMode,
    tmsOrder?.scanConfig.uploadedPalletShippingLabels,
  ]);

  const palletTableCols = useMemo(() => {
    return [...OrderProcessPalletCols, PalletDimensionCol, tableActionColumn];
  }, [tableActionColumn]);

  if (!riiOrderNo) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <PageHeader title="Please Input OTRII Order No." className="pb-4" />
        <div className="mb-4">
          <Alert
            message="Right now, only INSTA orders are supported!"
            type="warning"
            showIcon
          />
        </div>
        <Compact>
          <Input
            autoFocus
            size="large"
            placeholder="Scan or enter orrder no. starts with OTRII"
            className="!w-[420px]"
            allowClear
            onPressEnter={(e) => {
              const value = (e.target as HTMLInputElement).value.trim();
              if (value) {
                setRIIOrderNo(value);
              }
            }}
          />
          <Button size="large" type="primary" icon={<ScanOutlined />}>
            Scan (enter to input)
          </Button>
        </Compact>
      </div>
    );
  }

  if (!orderLoaded || tmsOrder == null) {
    return (
      <div className="flex h-full flex-col">
        <OrderLoadingDialog
          riiOrderNo={riiOrderNo}
          onLoaded={onOrderLoaded}
          onGoBack={() => setRIIOrderNo("")}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full flex-col overflow-y-auto global__no-scrollbar">
      <div className="shrink-0 flex flex-row items-center justify-between">
        <PageHeader title={pageHeaderText} className="shrink-0 pl-2" />
        <div className="shrink-0 h-full pt-1 flex  gap-2">
          <Button
            variant="solid"
            color="green"
            icon={<CheckOutlined color="green" />}
            onClick={() => {
              setRIIOrderNo("");
              setTMSOrder(null);
              setCubeOrder(null);
              setOrderLoaded(false);
            }}
          >
            Scan Another
          </Button>
          <PrinterConfig ref={printerConfigRef} />
        </div>
      </div>
      <Indicator label="Basic Info" />
      {tmsOrder != null && <BasicInfo order={tmsOrder} />}
      {tmsOrder != null && (
        <ScanInfo
          orderId={tmsOrder.id}
          orderStatus={tmsOrder.status}
          scanConfig={tmsOrder.scanConfig}
          onUploadDone={() => {
            // palletsTableRef.current?.refresh();
            // shippingLabelInfoRef.current?.refresh();
            refetchTMSOrder();
          }}
        />
      )}
      <Indicator
        label="Products"
        hint="Scroll horizontally to view all products"
      />
      <div
        className={classNames(
          "border-[1px] border-gray-200",
          "rounded-md p-2 bg-gray-200/50",
          "max-h-[200px] overflow-auto",
          "min-h-[130px]",
        )}
      >
        <CartonList orderId={tmsOrder?.id} ref={cartonListRef} />
      </div>
      <div className="flex flex-row gap-2 items-center justify-between">
        <div className="flex flex-row items-center gap-2">
          <Indicator label="Pallets" />
          <ShippingLabelInfo
            className="h-[32px]"
            orderId={tmsOrder.id}
            ref={shippingLabelInfoRef}
          />
        </div>
        <div className="flex flex-row gap-2 items-center">
          <Button
            className="mt-[8px]"
            icon={<ReloadOutlined />}
            onClick={() => {
              palletsTableRef.current?.refresh();
              shippingLabelInfoRef.current?.refresh();
            }}
          />
          <Button
            className="mt-[8px]"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              if (tmsOrder?.status === TMSOrderStatus.Processing) {
                palletAddDialogRef.current?.showModal({
                  orderId: tmsOrder.id,
                  groupId: tmsOrder.groups[0].id,
                  orderNo: tmsOrder.riiOrderNo,
                });
              } else {
                hintPrompt(
                  "error",
                  "Cannot add pallet",
                  "Order is not under processing!",
                );
              }
            }}
          >
            Add Pallet
          </Button>
          <Button
            className="mt-[8px]"
            icon={<DownloadOutlined />}
            onClick={() => {}}
          >
            Dimensions
          </Button>
        </div>
      </div>
      <TMSPalletsTable
        ref={palletsTableRef}
        className="mt-1 min-h-[540px] !rounded-md border-[1px] border-gray-200"
        cols={palletTableCols}
        query={palletQuery}
        minRowHeight={80}
        onQueryChange={() => {}}
      />
      <PalletAddDialog ref={palletAddDialogRef} onAddPallet={onAddPallet} />
      <PalletScanProducts
        ref={palletScanProductsDialogRef}
        onPrintCartonLabel={printCartonLabel}
        onScannedCarton={() => {
          cartonListRef.current?.refresh();
          palletsTableRef.current?.refresh();
          shippingLabelInfoRef.current?.refresh();
        }}
        onCompletePallet={(
          orderId,
          palletId,
          palletNo,
          requireDimension,
          printPalletLabel,
          printShippingLabel,
        ) => {
          palletCompleteConfirmDialogRef.current?.showModal({
            palletId,
            palletNo,
            orderId,
            requireDimension,
            printPalletLabel,
            printShippingLabel,
            completeCb: completePalletCb,
          } as PalletCompleteDialogOpenProps);
        }}
      />
      <PdfViewerModal ref={pdfViewerModalRef} />
      <PalletCompleteConfirmDialog ref={palletCompleteConfirmDialogRef} />
    </div>
  );
}

export const ProcessScanOrderPage = wrapPage(ProcessScanOrderPageFC);
